import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle } from "lucide-react";

const wins = [
  "Professionally rewritten",
  "Redesigned for impact",
  "Delivered in minutes by AI",
];

const diyProblems = [
  "You do all the writing",
  "Generic templates",
  "No ATS guarantee",
];

export const WhyDoneForYou = () => {
  return (
    <section
      id="why-done-for-you"
      className="relative py-24 sm:py-32"
      data-testid="why-done-for-you-section"
    >
      <div
        aria-hidden
        className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 h-[400px] w-[720px] rounded-full bg-gradient-to-r from-indigo-600/10 to-purple-600/10 blur-[140px] pointer-events-none"
      />

      <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-xl"
          >
            <p className="text-xs uppercase tracking-[0.18em] text-zinc-500">
              Done-for-you
            </p>
            <h2
              className="mt-3 text-3xl md:text-4xl font-semibold tracking-tight text-white text-balance"
              data-testid="why-headline"
            >
              Why done-for-you beats DIY resume builders
            </h2>
            <p
              className="mt-5 text-zinc-400 leading-relaxed text-balance"
              data-testid="why-body"
            >
              Most resume tools sell you a template and push the work onto you.
              CV AI PRO is different: we deliver a finished document, engineered
              for the systems 99% of Fortune 500 companies use to filter
              candidates and the recruiters who make the final call. From
              cluttered to clear. From generic to recruiter-ready. Your new
              resume is analyzed, rewritten, redesigned and delivered by AI in
              minutes — ready to send.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-4 text-sm">
              <Link
                to="/resume-writing-service"
                className="text-zinc-200 hover:text-white underline underline-offset-4 decoration-zinc-700 hover:decoration-white transition-colors"
                data-testid="why-link-rws"
              >
                Resume Writing Service →
              </Link>
              <Link
                to="/ai-resume-builder"
                className="text-zinc-200 hover:text-white underline underline-offset-4 decoration-zinc-700 hover:decoration-white transition-colors"
                data-testid="why-link-arb"
              >
                AI Resume Builder →
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="grid grid-cols-1 sm:grid-cols-2 gap-4"
          >
            <div
              className="relative rounded-2xl border border-white/10 bg-zinc-950/60 p-6"
              data-testid="why-diy-card"
            >
              <span className="inline-flex items-center gap-1.5 rounded-full bg-red-500/10 border border-red-500/20 px-2.5 py-1 text-[11px] text-red-300">
                DIY builders
              </span>
              <ul className="mt-5 space-y-3">
                {diyProblems.map((i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 text-sm text-zinc-400"
                  >
                    <XCircle className="h-4 w-4 text-red-400/80 mt-0.5 shrink-0" />
                    {i}
                  </li>
                ))}
              </ul>
            </div>

            <div
              className="relative rounded-2xl border border-white/10 bg-gradient-to-b from-zinc-950 to-black p-6 overflow-hidden"
              data-testid="why-cvaipro-card"
            >
              <div
                aria-hidden
                className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-blue-500/15 blur-3xl"
              />
              <span className="relative inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 px-2.5 py-1 text-[11px] text-emerald-300">
                CV AI PRO
              </span>
              <ul className="relative mt-5 space-y-3">
                {wins.map((i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 text-sm text-zinc-200"
                  >
                    <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
                    {i}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
